import { useState } from 'react';
import { Button, Label, Modal, TextInput, Textarea } from "flowbite-react";
import useSkillController from '../controllers/useSkillController';

function Skill() {
  const { 
    skills, 
    loading, 
    error, 
    addSkill, 
    updateSkill, 
    deleteSkill 
  } = useSkillController();

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSkill, setCurrentSkill] = useState(null);
  const [skillData, setSkillData] = useState({
    name: '',
    description: '',
    sub_skill: '',
    category: 'Hard Skill'
  });

  const onCloseModal = () => {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentSkill(null);
    setSkillData({
      name: '',
      description: '',
      sub_skill: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSkillData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditMode && currentSkill) {
      await updateSkill(currentSkill.id, skillData);
    } else {
      await addSkill(skillData);
    }
    onCloseModal();
  };

  const handleEdit = (skill) => {
    setCurrentSkill(skill);
    setIsEditMode(true);
    setSkillData({
      name: skill.name,
      description: skill.description,
      sub_skill: skill.sub_skill,
      category: skill.category
    });
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      await deleteSkill(id);
    }
  };

  return (
    <div className='flex'>
      <section className="flex-1 py-5 md:px-5 bg-gray-100 dark:bg-gray-900">
        {/* Header similar to Certificate */}
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className='my-6 md:col-span-2 md:pl-6'>
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Skills
              </span>
            </h1>
            <p className="max-w-3xl md:mt-6 mt-2 mr-4 text-gray-700 dark:text-white">
              Highlight your capabilities! Add skills that demonstrate your expertise and competencies.
            </p>
          </div>
          <div className="mx-auto my-auto md:col-span-1 ml-3">
            <Button color="blue" onClick={() => setOpenModal(true)}>+ Add Skill</Button>
          </div>
        </div>

        {/* Skills List */}
        <div className="space-y-4">
          {loading && <p>Loading...</p>}
          {error && <p className="text-red-500">{error}</p>}
          {skills.map(skill => (
            <div key={skill.id} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    {skill.name}
                  </h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                        skill.category === 'Hard Skill' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                        {skill.category}
                    </span>
                  {skill.description && (
                    <p className="mt-2 text-gray-600 dark:text-gray-300">
                      {skill.description}
                    </p>
                  )}
                  {skill.sub_skill && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Sub-skills: 
                      </span>
                      <span className="ml-2 text-gray-700 dark:text-gray-300">
                        {skill.sub_skill}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button color="gray" onClick={() => handleEdit(skill)}>
                    Edit
                  </Button>
                  <Button color="red" onClick={() => handleDelete(skill.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        <Modal show={openModal} onClose={onCloseModal}>
          <Modal.Header>
            {isEditMode ? 'Edit Skill' : 'Add New Skill'}
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Skill Name</Label>
                <TextInput
                  id="name"
                  name="name"
                  value={skillData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={skillData.description}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="sub_skill">Sub-skills ( ';' separated )</Label>
                <TextInput
                  id="sub_skill"
                  name="sub_skill"
                  value={skillData.sub_skill}
                  onChange={handleInputChange}
                  placeholder="e.g., React;Node.js;PostgreSQL"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <select
                    id="category"
                    name="category"
                    value={skillData.category}
                    onChange={handleInputChange}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                >
                    <option value="">Select Category</option>
                    <option value="Hard Skill">Hard Skill</option>
                    <option value="Soft Skill">Soft Skill</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <Button color="gray" onClick={onCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" color="blue">
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Skill;